import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Columns3, GripVertical, RotateCcw } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import type { ContactTableColumnId } from "./contacts-column-view.js";
import { contactsViewportPopoverClass } from "./contacts-table-presentation.js";

export function ContactsColumnsPanel({
  columnOrder,
  hiddenColumns,
  onToggle,
  onMove,
  onReorder,
  onReset,
}: {
  columnOrder: readonly ContactTableColumnId[];
  hiddenColumns: ReadonlySet<ContactTableColumnId>;
  onToggle: (id: ContactTableColumnId) => void;
  onMove: (id: ContactTableColumnId, direction: "up" | "down") => void;
  onReorder: (fromId: ContactTableColumnId, toId: ContactTableColumnId) => void;
  onReset: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draggingId, setDraggingId] = useState<ContactTableColumnId | null>(null);
  const [dropId, setDropId] = useState<ContactTableColumnId | null>(null);

  return (
    <div className="grid gap-1" data-testid="contacts-columns-panel">
      {columnOrder.map((columnId, index) => {
        const visible = !hiddenColumns.has(columnId);
        const label = t(`contacts.columns.${columnId}`);
        return (
          <div
            key={columnId}
            className={cn(
              "flex items-center gap-1 rounded-md",
              dropId === columnId && draggingId != null && draggingId !== columnId
                ? "bg-accent/10"
                : null,
            )}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setDropId(columnId);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const fromRaw = event.dataTransfer.getData("text/plain") as ContactTableColumnId;
              const from = columnOrder.includes(fromRaw) ? fromRaw : draggingId;
              setDraggingId(null);
              setDropId(null);
              if (from) onReorder(from, columnId);
            }}
          >
            <span
              draggable
              className={cn(
                "inline-grid size-7 shrink-0 select-none place-items-center text-muted",
                draggingId === columnId ? "cursor-grabbing" : "cursor-grab",
              )}
              aria-label={t("contacts.view.reorderColumn", { column: label })}
              data-testid="contacts-column-drag-handle"
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", columnId);
                setDraggingId(columnId);
              }}
              onDragEnd={() => {
                setDraggingId(null);
                setDropId(null);
              }}
            >
              <GripVertical size={14} aria-hidden="true" />
            </span>
            <label className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-[0.8125rem]">
              <input
                type="checkbox"
                checked={visible}
                onChange={() => onToggle(columnId)}
                className="size-4 accent-[var(--color-accent)]"
                data-testid={`contacts-column-toggle-${columnId}`}
              />
              <span className="min-w-0 truncate">{label}</span>
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 p-0"
              disabled={index === 0}
              aria-label={t("contacts.view.moveUp", { column: label })}
              onClick={() => onMove(columnId, "up")}
            >
              <ArrowUp size={14} aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 p-0"
              disabled={index === columnOrder.length - 1}
              aria-label={t("contacts.view.moveDown", { column: label })}
              onClick={() => onMove(columnId, "down")}
            >
              <ArrowDown size={14} aria-hidden="true" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 h-8 justify-start"
        onClick={onReset}
        data-testid="contacts-reset-columns"
      >
        <RotateCcw size={14} aria-hidden="true" />
        {t("contacts.view.reset")}
      </Button>
    </div>
  );
}

export function ContactsColumnsPopover(
  props: Parameters<typeof ContactsColumnsPanel>[0],
): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-[0.75rem]"
          data-testid="contacts-columns"
        >
          <Columns3 size={14} aria-hidden="true" />
          {t("contacts.actions.view")}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={12}
        className={cn("w-[min(18rem,calc(100vw-1.5rem))] p-3", contactsViewportPopoverClass)}
      >
        <ContactsColumnsPanel {...props} />
      </PopoverContent>
    </Popover>
  );
}
